import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  SearchPropertiesOptions,
  ListPropertiesResults,
} from "../../../types/neighborhoodSearch";
import { geosearchApi, addressSearchApi } from "../../../libs/landvoice-api";
import { getGeoSearchParams } from "../utils/getGeoSearchParams";
import { getNsSearchPropertiesCacheKey } from "../constants/cacheKeys";

const MONTHLY_LIMIT_API_MESSAGE = 'Accumulated lookups exceed the monthly limit';
const MONTHLY_LIMIT_USER_MESSAGE = "You've reached your monthly search limit. Please contact us to purchase additional credits.";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformErrorMessage = (error: any): Error => {
  const apiMessage = error?.response?.data?.detail || "An error occurred while searching. Please try again.";
  
  // Transform specific API error messages
  if (apiMessage.includes(MONTHLY_LIMIT_API_MESSAGE)) {
    const transformedError = new Error(MONTHLY_LIMIT_USER_MESSAGE);
    transformedError.name = "MonthlyLimitExceeded";
    return transformedError;
  }
  
  const transformedError = new Error(apiMessage);
  transformedError.name = "Unexpected";
  return transformedError;
};

export const useSearchProperties = (
  listOptions: SearchPropertiesOptions | null
): UseQueryResult<ListPropertiesResults> => {
  return useQuery({
    queryKey: getNsSearchPropertiesCacheKey(listOptions),
    queryFn: async () => {
      const { geo, address } = listOptions || {};

      if (geo) {
        const {
          bedrooms,
          bathrooms,
          acreage,
          squareFootage,
          yearBuilt,
          propertyType,
          companyOwned,
        } = getGeoSearchParams(listOptions);

        try {
          const geoResults = await geosearchApi.geosearchPreview(
            geo,
            bedrooms,
            bathrooms,
            acreage,
            squareFootage,
            yearBuilt,
            propertyType,
            companyOwned
          );
          return geoResults?.data;
        } catch (error) {
          throw transformErrorMessage(error);
        }
      } else if (address) {
        try {
          const addressSearchResults =
            await addressSearchApi.reverseAddressPreview({
              addresses: [
                {
                  house_number: address.houseNumber,
                  street_name: address.streetName,
                  street_suffix: address.streetSuffix,
                  city: address.city,
                  state: address.state,
                  postal_code: address.postalCode,
                  unit: address.unit,
                },
              ],
            });
          return addressSearchResults?.data;
        } catch (error) {
          throw transformErrorMessage(error);
        }
      }
      return { properties: [], count: 0 };
    },
    enabled: !!(
      listOptions?.geo ||
      listOptions?.address
    ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};